/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { PageLink, 
  PageTitle 
} from '../../../../_metronic/layout/core/PageData'
import { useReportGetByIdMutation, useReportDeleteByIdMutation, useReportDowloadByIdMutation, useReportAttachmentSearchMutation, useReportShareByIdMutation, useReportDowloadAcceptanceByIdMutation, useReportTicketAttachmentSearchMutation } from '../Report.api'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { IReportDto, IReportFilter, IReportShareLogDto } from '../Report.model'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { DeleteConfirmationModal } from '../../../common/component/modal/DeleteConfirmationModal'
import { IReportAttachmentDto } from '../ReportAttachment.model'
import { FilePicker } from '../../../common/component/FilePicker'
import { IAttachment } from '../../media/Media.model'
import { ShareConfirmationModal } from '../../../common/component/modal/ShareConfirmationModal'
import { EMAIL_REGEX } from '../../../util/Regex'
import { Toast } from 'bootstrap'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { getLanguage, getTimeZone, parseLocalDate } from '../../../util/Locale'
import { FREQUENCY, renderIdentifier } from '../../../util/Constants'
import { ContactEditor } from '../../contact/components/ContactEditor'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { AssetEmbedered } from '../../asset/components/AssetEmbedered'
import { TicketEmbedered } from '../../helpdesk/ticket/components/TicketEmbedered'

const ReportDetailsPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {reportId} = useParams()

  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/reports/${reportId}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.REPORT.ACCEPTANCE.EXPORT'}),
      icon: 'bi-filetype-pdf',
      action: () => downloadAcceptancePdf(reportId),
    },
    {
      label: intl.formatMessage({id: 'UTIL.REPORT.CONTENT.EXPORT'}),
      icon: 'bi-filetype-pdf',
      action: () => downloadPdf(reportId),
    },
  ]
  const shareSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.SHARE'}),
    icon: 'bi-envelope-at',
    targetModal: '#report_share_confirmation_modal',
  }
  const removeSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.DELETE'}),
    icon: 'bi-trash',
    targetModal: '#report_delete_confirmation_modal',
  }
  
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.REPORTS'}),
      path: '/reports',
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [openDropDown, setOpenDropDown] = useState(false)

  const didFetchDownload = useRef(false)
  const didRequestDelete = useRef(false)
  const didRequestShare = useRef(false)
  const didFetchReport = useRef(false)
  const [report, setReport] = useState<IReportDto | undefined>()
  const [isReportLoading, setIsReportLoading] = useState(true)
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [reportGetById] = useReportGetByIdMutation()
  const [reportDeleteById] = useReportDeleteByIdMutation()
  const [reportDownloadAcceptanceById] = useReportDowloadAcceptanceByIdMutation()
  const [reportDownloadById] = useReportDowloadByIdMutation()
  const [reportShareById] = useReportShareByIdMutation()

  const didFetchAttachments = useRef(false)
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false)
  const [attachments, setAttachments] = useState<IReportAttachmentDto[]>()
  const [reportAttachmentSearch] = useReportAttachmentSearchMutation()

  const didFetchTicketAttachments = useRef(false)
  const [isTicketAttachmentsLoading, setIsTicketAttachmentsLoading] = useState(false)
  const [ticketAttachments, setTicketAttachments] = useState<IReportAttachmentDto[]>()
  const [reportTicketAttachmentSearch] = useReportTicketAttachmentSearchMutation()

  const [shared, setShared] = useState<boolean>(false)
  const [sharedLogs, setSharedLogs] = useState<IReportShareLogDto[] | undefined>()

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])
  
  useEffect(() => {
    const fetchReport = async (reportId) => {
      try {
        if (!didFetchReport.current) {
          setIsReportLoading(true)
          const response: any = await reportGetById({id: reportId})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const report: IReportDto = response.data
            setReport(report)
            searchAttachment(report.id)
            searchTicketAttachment(report.id)
            
            if (report.sharedLogs) {
              setShared(report.shared)
              setSharedLogs(report.sharedLogs)
            }
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsReportLoading(false)
      }
    }

    const searchAttachment = async (reportId) => {
      try {
        if (!didFetchAttachments.current) {
          setIsAttachmentsLoading(true)
          const response: any = await reportAttachmentSearch({
            filters: [{id: IReportFilter.HASH, value: reportId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            setAttachments(response.data.data)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAttachmentsLoading(false)
      }
    }

    const searchTicketAttachment = async (reportId) => {
      try {
        if (!didFetchTicketAttachments.current) {
          setIsTicketAttachmentsLoading(true)
          const response: any = await reportTicketAttachmentSearch({
            filters: [{id: IReportFilter.HASH, value: reportId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            setTicketAttachments(response.data.data)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsTicketAttachmentsLoading(false)
      }
    }

    if (reportId) {
      fetchReport(reportId)
    }
  }, [])

  const deleteConfirmOnClickHandler = async () => {
    try {
      if (reportId && !didRequestDelete.current) {
        setIsReportLoading(true)
        const response: any = await reportDeleteById(reportId)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          navigate(`/reports`)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsReportLoading(false)
    }
  }

  const downloadAcceptancePdf = async (reportId) => {
    try {
      if (reportId) {
        setIsDownloadLoading(true)
        if (!didFetchDownload.current) {
          const downloadResponse = await reportDownloadAcceptanceById({
            id: reportId,
            header: true,
            i18n: getLanguage(),
            timeZone: getTimeZone()
          }).unwrap()
          if (downloadResponse) {
            const url = downloadResponse.url
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = url
            link.download = downloadResponse.file
            link.click()
          }
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsDownloadLoading(false)
    }
  }

  const downloadPdf = async (reportId) => {
    try {
      if (reportId) {
        setIsDownloadLoading(true)
        if (!didFetchDownload.current) {
          const downloadResponse = await reportDownloadById({
            id: reportId,
            header: false,
            i18n: getLanguage(),
            timeZone: getTimeZone()
          }).unwrap()
          if (downloadResponse) {
            const url = downloadResponse.url;
            const link = document.createElement('a');
            link.target = '_blank';
            link.href = url;
            link.download = downloadResponse.file;
            link.click();
          }
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsDownloadLoading(false)
    }
  }

  const shareSubmitOnClickHandler = async (recipients: string[], comments?: string) => {
    try {
      if (reportId && !didRequestShare.current && recipients.length > 0) {
        setIsReportLoading(true)
        const response: any = await reportShareById({
          id: reportId,
          to: recipients,
          i18n: getLanguage(),
          timeZone: getTimeZone(),
          comments,
        }).unwrap()
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          if (report) {
            setShared(true)
            if (sharedLogs) {
              setSharedLogs([...sharedLogs, response])
            } else {
              setSharedLogs([response])
            }
          } 

          setToastEntry([
            MessageType.SUCCESS,
            intl
              .formatMessage({id: 'UTIL.SHARE.SUCCESS.MESSAGE'})
              .replace(/%s/g, intl.formatMessage({id: 'UTIL.REPORT'})),
          ])
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsReportLoading(false)
    }
  }

  const getContactToList = (report: IReportDto): string[] => {
    const toList: string[] = [];
    if (report.contacts) {
      report.contacts.map(contact => {
        if (contact.email) {
          const value = `${contact.email}`.toLowerCase()
          if (!toList.includes(value)) {
            toList.push(value)
          }
        } else if (contact.details) {
          contact.details.filter(detail => 
            detail.type === 'EMAIL' 
            && detail.value 
            && detail.value.match(EMAIL_REGEX)
          ).map(detail => {
            const value = `${detail.value}`.toLowerCase();
            if (!toList.includes(value)) {
              toList.push(value)
            }
          })
        }
      })
    }
    return toList;
  }

  const renderDeliveryRecords = (shareLogs: IReportShareLogDto[]) => {
    if (shareLogs) {
        // Sort by date
        const logs = shareLogs.map(shareLog => {
          return {
            createdAt: new Date(Date.parse(shareLog.createdAt)),
            createdBy: shareLog.createdBy,
            from: shareLog.from,
            to: shareLog.to,
          }
        })
        logs.sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf() );

        return logs.map((sharedLog, i) => {
          return (
            <div key={i}>
              <div className='separator my-2'></div>
              <div>
                <div className='d-flex flex-stack'>
                  <div className='d-flex align-items-center'>
                    <div>
                      <div className='lh-2 fs-7 cursor-pointer'>
                        <span className='fw-bolder mb-2'>
                          {sharedLog.createdAt.toLocaleString()}
                        </span>
                      </div>

                      <div className='mt-2 lh-1'>
                        <div className='d-flex justify-content-start'>
                          <div className='d-flex align-items-center fs-8'>
                            <span className='text-gray-800'>
                              {intl.formatMessage({id: 'UTIL.SENDER'})}:&nbsp;
                              {sharedLog.from?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='mt-2 fs-8'>
                          {intl.formatMessage({id: 'UTIL.RECIPIENTS'})}:&nbsp;
                          {sharedLog.to &&
                            sharedLog.to.map((to, i) => {
                              if (i + 1 == sharedLog.to.length) {
                                return <span key={i} className='text-gray-600'>{to?.toUpperCase()}</span>
                              } else {
                                return <span key={i} className='text-gray-600'>{to?.toUpperCase()}, </span>
                              }
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {report && (
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fs-2 fw-bolder'>
                        {`${parseLocalDate(
                          report.createdAt,
                          intl.formatMessage({id: 'I18N.LOCALE'})
                        )} - ${report.client.displayName}`}
                      </span>
                      <span className='fw-bolder text-primary me-1 fs-5'>
                        {renderIdentifier(report.sequence)}
                      </span>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-row-auto flex-start ms-5'>
                  {report && (
                    <>
                      <button
                        className='btn btn-sm btn-light-primary me-2'
                        onClick={() => setOpenDropDown((prev) => !prev)}
                        disabled={isDownloadLoading || isReportLoading}
                      >
                        {isDownloadLoading ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle'></span>
                          </span>
                        ) : (
                          <i className='bi bi-three-dots fs-5'></i>
                        )}
                      </button>
                      <DropdownMenu
                        visible={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        settingOptions={settingOptions}
                        shareOption={shareSettingOption}
                        deleteOption={removeSettingOption}
                      ></DropdownMenu>
                    </>
                  )}

                  <button className='btn btn-sm btn-light' onClick={() => navigate(`/reports`)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isReportLoading ? (
            <Spinner />
          ) : !report ? (
            <div className='row'>
              <div className='d-flex flex-row-fluid flex-start mt-10'>
                <span className='text-danger '>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    <span className='fs-6 me-1'>
                      <i className='text-danger me-1 bi bi-exclamation-octagon' />
                      {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                      {intl.formatMessage({id: 'UTIL.TECHNICIANS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                      {intl.formatMessage({id: 'UTIL.REPORT'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_5'>
                      {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    </a>
                  </li>
                  {/* HIDDEN SPARE PARTS FEATURE TAB FOR NOW */}
                  {/* {report.workOrderRequest &&
                    report.workOrderRequest.lines &&
                    report.workOrderRequest.lines.length > 0 &&                    
                    (!report.ticketReferences || report.ticketReferences.length === 0) && (
                      <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_6'>
                          {intl.formatMessage({id: 'UTIL.SPARE_PARTS'})}
                        </a>
                      </li>
                    )} */}
                  {sharedLogs && sharedLogs.length > 0 && (
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_7'>
                        {intl.formatMessage({id: 'UTIL.HISTORY'})}
                      </a>
                    </li>
                  )}
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <ClientEmbedered client={report.client} />
                        {report.asset && <AssetEmbedered asset={report.asset} />}
                      </div>
                    </div>

                    {report.ticketReferences && report.ticketReferences.length > 0 && (
                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({
                            id: 'REPORT.WORK_ORDER_REQUEST.COMPLETE.LABEL',
                          })}
                        />
                        <div className='px-5'>
                          <div className='row mb-5'>
                            {report.ticketReferences.map((ticket) => (
                              <TicketEmbedered key={ticket.id} ticketId={ticket.id!} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.REPORT'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.VERSION'})}
                            value={`${report.reportMetadata.sequence}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div> */}

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                            value={parseLocalDate(
                              report.createdAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                            value={parseLocalDate(
                              report.updatedAt,
                              intl.formatMessage({id: 'I18N.LOCALE'})
                            )}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ID'})}
                            value={`${report.sequence}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_DESK'})}
                            value={`${
                              report.serviceDesk
                                ? intl.formatMessage({id: 'UTIL.YES'})
                                : intl.formatMessage({id: 'UTIL.NO'})
                            }`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.STATUS'})}
                            value={
                              report.status
                                ? intl.formatMessage({
                                    id: `REPORT.STATUS.${report.status}`,
                                  })
                                : ''
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                            value={parseLocalDate(report.startedAt, '', '', {
                              hours: true,
                              minutes: true,
                              seconds: false,
                              millis: false,
                            })}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                            value={parseLocalDate(report.endedAt, '', '', {
                              hours: true,
                              minutes: true,
                              seconds: false,
                              millis: false,
                            })}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                            value={report.visitType}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                            value={`${
                              report.frequencyCode === FREQUENCY.OTHER
                                ? report.frequency
                                : intl.formatMessage({
                                    id: `DROPDOWN.FREQUENCY.${report.frequencyCode}`,
                                  })
                            }`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CATEGORY'})}
                            value={report.category}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            value={report.country}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            value={report.address}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.METRICS'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                            value={`${report.totalAttachment || 0}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <div className='row mb-5'>
                          <ContactEditor data={report.contacts} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <div className='row mb-5'>
                          <ContactEditor
                            data={report.technicians}
                            primaryLabel={intl.formatMessage({id: 'UTIL.PRIMARY_TECHNICIAN'})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                    <div className='row mb-5'>
                      {report && report.metadata && (
                        <ReportRenderer
                          collection={report.metadata.items}
                          path={`metadata.items`}
                          disabled={true}
                          parameters={{FINAL_VERSION: report.status === 'COMPLETED'}}
                        />
                      )}
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_5' role='tabpanel'>
                    <div className='row mb-5'>
                      <FilePicker
                        loading={isAttachmentsLoading}
                        deleteConfirmationModalId='report-details-attachments-delete-confirmation-modal'
                        uploadedFiles={attachments?.map((attachment) => {
                          const file: IAttachment = {
                            id: attachment.id,
                            fileName: attachment.fileName,
                            temporalPath: attachment.temporalPath,
                            path: attachment.path,
                            size: attachment.size,
                            contentType: attachment.contentType,
                            comments: attachment.comments,
                            status: attachment.status,
                            processing: false,
                            uploaded: true,
                          }
                          return file
                        })}
                      />
                    </div>
                  </div>
                  {report.workOrderRequest &&
                    report.workOrderRequest.lines &&
                    report.workOrderRequest.lines.length > 0 &&
                    (!report.ticketReferences || report.ticketReferences.length === 0) && (
                      <div className='tab-pane fade' id='kt_tab_pane_6' role='tabpanel'>
                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({
                              id: 'REPORT.WORK_ORDER_REQUEST.DETAILS.HEADER',
                            })}
                          />

                          <div className='px-5'>
                            <div className='row mb-5'>
                              {report.workOrderRequest?.lines &&
                              report.workOrderRequest.lines.length > 0
                                ? report.workOrderRequest.lines.map((line, index) => (
                                    <div key={index} className='row'>
                                      <CustomInputRenderer
                                        label={intl.formatMessage({
                                          id: `${`WORK_ORDER_LINE_TYPE.${line.workOrderTypeCode}`}`,
                                        })}
                                        value={line.details.toUpperCase()}
                                      />
                                    </div>
                                  ))
                                : null}
                            </div>
                          </div>
                        </div>

                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                          />

                          <div className='px-5'>
                            <div className='row mb-5'>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.PRIORITY_LEVEL'})}
                                value={
                                  report.workOrderRequest?.priorityCode
                                    ? intl.formatMessage({
                                        id: `TICKET.PRIORITY.${report.workOrderRequest?.priorityCode}`,
                                      })
                                    : ''
                                }
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({
                                  id: 'REPORT.WORK_ORDER_REQUEST.NOTES.LABEL',
                                })}
                                object={
                                  <textarea
                                    rows={4}
                                    className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                    value={
                                      report.workOrderRequest?.observations?.toUpperCase() || ''
                                    }
                                    disabled={true}
                                  />
                                }
                              ></CustomInputRenderer>
                            </div>
                          </div>
                        </div>

                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                          />

                          <div className='px-5'>
                            {/* <div className='row mb-5'></div> */}
                            <FilePicker
                              loading={isTicketAttachmentsLoading}
                              deleteConfirmationModalId='report-details-ticket-attachments-delete-confirmation-modal'
                              uploadedFiles={ticketAttachments?.map((attachment) => {
                                const file: IAttachment = {
                                  id: attachment.id,
                                  fileName: attachment.fileName,
                                  temporalPath: attachment.temporalPath,
                                  path: attachment.path,
                                  size: attachment.size,
                                  contentType: attachment.contentType,
                                  comments: attachment.comments,
                                  status: attachment.status,
                                  processing: false,
                                  uploaded: true,
                                }
                                return file
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  {sharedLogs && sharedLogs.length > 0 && (
                    <div className='tab-pane fade' id='kt_tab_pane_7' role='tabpanel'>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          title={intl.formatMessage({id: 'UTIL.MAIL_DELIVERY_RECORD'})}
                          separator={false}
                        />
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <div className='overflow-auto' style={{maxHeight: 400}}>
                              {renderDeliveryRecords(sharedLogs)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DeleteConfirmationModal
                id='report_delete_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                  .replace(/%s/g, intl.formatMessage({id: 'UTIL.REPORT'}))}
                description={`${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
                  report.client.displayName
                } - #${report?.sequence}`}
                confirmOnClickHandler={deleteConfirmOnClickHandler}
              ></DeleteConfirmationModal>

              <ShareConfirmationModal
                id='report_share_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.SHARE.CONFIRMATION.TITLE'})
                  .replace(
                    /%s/g,
                    `${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
                      report.client.displayName
                    } - #${report?.sequence}`
                  )}
                description={`${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
                  report.client.displayName
                } - #${report?.sequence}`}
                toListContacts={getContactToList(report)}
                submitOnClickHandler={shareSubmitOnClickHandler}
              ></ShareConfirmationModal>
            </>
          )}
        </div>
      </div>
    </>
  )

}

export { ReportDetailsPage }
